import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Clock, CheckCircle2, Circle, XCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { useMyApplications } from "@/hooks/useJobApplications";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { APPLICATION_STATUS_CONFIG, PIPELINE_STEPS, getStatusLabel } from "@/lib/applicationStatus";

export default function AlunoCandidaturaDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { applications, isLoading: appsLoading } = useMyApplications();
  const [showRetirarModal, setShowRetirarModal] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const application = applications.find((a) => a.id === id);
  const job = application?.job_posts as any;
  const org = job?.partner_orgs;

  // Fetch status events for this application
  const { data: events } = useQuery({
    queryKey: ["application_events_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("application_status_events")
        .select("*")
        .eq("application_id", id!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const handleWithdraw = async () => {
    if (!application || !user) return;
    setWithdrawing(true);
    try {
      const { error: updateErr } = await supabase
        .from("job_applications")
        .update({ status: "desistiu" })
        .eq("id", application.id);
      if (updateErr) throw updateErr;

      await supabase.from("application_status_events").insert({
        application_id: application.id,
        from_status: application.status,
        to_status: "desistiu",
        actor_user_id: user.id,
        note: withdrawReason || "Candidatura retirada pelo aluno",
      });

      toast({ title: "Candidatura retirada", description: "Sua candidatura foi retirada com sucesso." });
      navigate("/aluno/vagas/candidaturas");
    } catch (err: any) {
      toast({ title: "Erro ao retirar", description: err.message, variant: "destructive" });
    } finally {
      setWithdrawing(false);
    }
  };

  if (appsLoading) {
    return <div className="page-container flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!application) {
    return (
      <div className="page-container text-center py-16">
        <p className="text-muted-foreground">Candidatura não encontrada.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/aluno/vagas/candidaturas")}>Voltar</Button>
      </div>
    );
  }

  const currentStatus = application.status;
  const isTerminal = ["contratado", "nao_avancou", "desistiu"].includes(currentStatus);

  // Build timeline from events
  const eventsByStatus = new Map<string, { date: string; note?: string }>();
  (events ?? []).forEach((e) => {
    eventsByStatus.set(e.to_status, { date: format(new Date(e.created_at), "dd/MM/yyyy"), note: e.note ?? undefined });
  });

  const currentStepIndex = PIPELINE_STEPS.indexOf(currentStatus);

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/vagas/candidaturas")}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Detalhe da candidatura</span>
      </motion.div>

      {/* Vaga info */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="w-7 h-7 text-primary" />
          </div>
          <div className="flex-1">
            <Badge variant="apprentice" className="mb-2">{job?.employment_type ?? ""}</Badge>
            <h1 className="font-heading text-lg font-bold text-foreground">{job?.title ?? "Vaga"}</h1>
            <p className="text-muted-foreground">{org?.name ?? ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          Aplicação em {format(new Date(application.created_at), "dd/MM/yyyy")}
        </div>
      </motion.div>

      {/* Timeline */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-4">Histórico do Processo</h2>
        <div className="relative">
          <div className="absolute left-[15px] top-6 bottom-6 w-0.5 bg-border" />
          <div className="space-y-4">
            {PIPELINE_STEPS.map((step, i) => {
              const ev = eventsByStatus.get(step);
              const isCurrent = step === currentStatus;
              const isCompleted = currentStepIndex >= 0 ? i < currentStepIndex : !!ev;
              const isReached = isCompleted || isCurrent;

              return (
                <div key={step} className="flex items-start gap-4 relative">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${
                    isCompleted ? "bg-success text-success-foreground"
                    : isCurrent ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : <Circle className={`w-4 h-4 ${isCurrent ? "fill-current" : ""}`} />}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className={`text-sm font-medium ${isReached ? "text-foreground" : "text-muted-foreground"}`}>
                      {getStatusLabel(step)}
                    </p>
                    <p className="text-xs text-muted-foreground">{ev?.date ?? "-"}</p>
                    {ev?.note && <p className="text-xs text-muted-foreground italic mt-0.5">{ev.note}</p>}
                  </div>
                </div>
              );
            })}

            {/* Terminal statuses */}
            {(currentStatus === "nao_avancou" || currentStatus === "desistiu") && (
              <div className="flex items-start gap-4 relative">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 bg-destructive text-destructive-foreground">
                  <XCircle className="w-4 h-4" />
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm font-medium text-foreground">{getStatusLabel(currentStatus)}</p>
                  <p className="text-xs text-muted-foreground">{eventsByStatus.get(currentStatus)?.date ?? "-"}</p>
                  {eventsByStatus.get(currentStatus)?.note && (
                    <p className="text-xs text-muted-foreground italic mt-0.5">{eventsByStatus.get(currentStatus)!.note}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
        <Button variant="outline" size="lg" className="w-full" onClick={() => navigate(`/aluno/vagas/${application.job_id}`)}>
          Ver detalhes da vaga
        </Button>
        {!isTerminal && (
          <Button variant="ghost" size="lg" className="w-full text-destructive hover:text-destructive" onClick={() => setShowRetirarModal(true)}>
            <XCircle className="w-4 h-4" /> Retirar candidatura
          </Button>
        )}
      </motion.div>

      {/* Modal Retirar */}
      {showRetirarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-card rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-heading text-lg font-bold text-foreground mb-2">Retirar candidatura?</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Tem certeza que deseja retirar sua candidatura para "{job?.title}"? Esta ação não pode ser desfeita.
            </p>
            <div className="mb-4">
              <label className="text-sm font-medium text-foreground mb-2 block">Motivo (opcional)</label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
              >
                <option value="">Selecione um motivo</option>
                <option value="Consegui outra oportunidade">Consegui outra oportunidade</option>
                <option value="Estou indisponível no momento">Estou indisponível no momento</option>
                <option value="Mudei de interesse">Mudei de interesse</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowRetirarModal(false)}>Cancelar</Button>
              <Button variant="destructive" className="flex-1" disabled={withdrawing} onClick={handleWithdraw}>
                {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
